import type { AppRouteRecordRaw } from '@/types/router'
import { pathResolve } from '@/utils/routerHelper'
import { usePermissionStoreWithOut } from '@/store/modules/permission'

/** 历史默认看板路径（库内 path=/statDashboard 且一级 leaf 包装后的常见形态） */
export const PREFERRED_HOME_PATH = '/statDashboard/index'

/** 欢迎页路径（无数据看板权限时的默认落地页） */
export const WELCOME_HOME_PATH = '/home'

/** 看板菜单编码，与 base_menu.code / 路由 name 一致 */
export const PREFERRED_HOME_ROUTE_NAME = 'statDashboard'

/** 不应作为登录后/404 返回目标的入口路径 */
const BLOCKED_ENTRY_PATHS = new Set(['/404', '/403', '/500', '/login'])

const SKIP_ROUTE_NAMES = new Set(['404Page', 'Redirect', 'RedirectWrap'])

const LEGACY_DASHBOARD_PATHS = new Set([
  PREFERRED_HOME_PATH,
  '/statDashboard',
  '/statData/statDashboard'
])

function isSkippableRoute(route: AppRouteRecordRaw): boolean {
  if (!route) return true
  if (route.name && SKIP_ROUTE_NAMES.has(String(route.name))) return true
  return String(route.path || '').includes('(.*)')
}

function resolveRouteFullPath(parentPath: string, routePath: string): string {
  if (!routePath) return parentPath
  if (routePath.startsWith('/')) return routePath.replace(/\/+/g, '/').trim()
  return pathResolve(parentPath, routePath).replace(/\/+/g, '/').trim()
}

function isPreferredHomeRouteName(name: unknown): boolean {
  if (!name) return false
  return String(name).localeCompare(PREFERRED_HOME_ROUTE_NAME, undefined, {
    sensitivity: 'accent'
  }) === 0
}

/**
 * 从已注册动态路由中解析看板真实可访问 path（对齐库内 path，如 /cockpit/StatDashboard）。
 * 优先按路由 name=statDashboard 匹配，其次按 redirect 中含 StatDashboard 兜底。
 */
function findPreferredHomePath(routes: AppRouteRecordRaw[], parentPath = ''): string | null {
  for (const route of routes) {
    if (isSkippableRoute(route)) continue
    const fullPath = resolveRouteFullPath(parentPath, route.path || '')

    if (isPreferredHomeRouteName(route.name)) {
      if (typeof route.redirect === 'string' && route.redirect.trim()) {
        return route.redirect.trim()
      }
      return fullPath || null
    }

    if (route.children?.length) {
      const childPath = findPreferredHomePath(route.children, fullPath)
      if (childPath) return childPath
    }
  }
  return null
}

/** 目录节点 redirect 指向看板时兜底（如 /cockpit → /cockpit/StatDashboard） */
function findPreferredHomePathByRedirect(routes: AppRouteRecordRaw[]): string | null {
  for (const route of routes) {
    if (isSkippableRoute(route)) continue
    const redirect = route.redirect
    if (typeof redirect === 'string') {
      const target = redirect.trim()
      if (target && /statdashboard/i.test(target)) {
        return target
      }
    }
    if (route.children?.length) {
      const childPath = findPreferredHomePathByRedirect(route.children)
      if (childPath) return childPath
    }
  }
  return null
}

/** 在已注册动态路由中判断用户是否可访问数据看板 */
export function hasPreferredHome(routes: AppRouteRecordRaw[]): boolean {
  if (findPreferredHomePath(routes) || findPreferredHomePathByRedirect(routes)) {
    return true
  }

  const walk = (items: AppRouteRecordRaw[], parentPath = ''): boolean => {
    for (const route of items) {
      if (isSkippableRoute(route)) continue
      const fullPath = resolveRouteFullPath(parentPath, route.path || '')
      if (
        LEGACY_DASHBOARD_PATHS.has(fullPath) ||
        isPreferredHomeRouteName(route.name) ||
        (typeof route.redirect === 'string' && LEGACY_DASHBOARD_PATHS.has(route.redirect.trim()))
      ) {
        return true
      }
      if (route.children?.length && walk(route.children, fullPath)) {
        return true
      }
    }
    return false
  }
  return walk(routes)
}

/**
 * 解析登录后 / 访问根路径时的默认落地页：
 * 1. 有数据看板 → 从动态路由解析真实 path
 * 2. 否则 → 欢迎页 /home
 */
export function resolveDefaultHomePath(routes?: AppRouteRecordRaw[]): string {
  const permissionStore = usePermissionStoreWithOut()
  const dynamicRoutes = routes ?? permissionStore.getAddRouters

  const byName = findPreferredHomePath(dynamicRoutes)
  if (byName) return byName

  const byRedirect = findPreferredHomePathByRedirect(dynamicRoutes)
  if (byRedirect) return byRedirect

  if (hasPreferredHome(dynamicRoutes)) {
    return PREFERRED_HOME_PATH
  }
  return WELCOME_HOME_PATH
}

/**
 * 登录/404 等场景的入口路径：过滤 /404 等无效 redirect，再回落到默认首页。
 * 欢迎页 /home、根路径、历史看板路径一律改走「有权限则看板」解析，避免登录落到空白欢迎页。
 */
export function resolveSafeEntryPath(redirect?: string, routes?: AppRouteRecordRaw[]): string {
  const raw = (redirect || '').trim()
  if (!raw) {
    return resolveDefaultHomePath(routes)
  }
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    decoded = raw
  }
  decoded = decoded.trim()
  // 首页类入口统一交给默认首页解析（有看板权限 → 看板，否则欢迎页）
  if (
    !decoded ||
    decoded === '/' ||
    decoded === WELCOME_HOME_PATH ||
    LEGACY_DASHBOARD_PATHS.has(decoded) ||
    /^\/statDashboard(\/index)?$/i.test(decoded) ||
    /^\/statData\/statDashboard/i.test(decoded)
  ) {
    return resolveDefaultHomePath(routes)
  }
  if (BLOCKED_ENTRY_PATHS.has(decoded) || decoded.includes('(.*)')) {
    return resolveDefaultHomePath(routes)
  }
  return decoded
}

/** 欢迎页路由 name（常量路由 Root 下） */
export const WELCOME_HOME_ROUTE_NAME = 'Home'

/**
 * 两个「首页」互斥：有看板权限只展示看板，无权限只展示欢迎页 /home
 * 须隐藏整个 Root 常量路由，否则 Home 隐藏后 Root 仍会作为「首页」出现在侧栏
 */
export function applyHomeMenuExclusion(
  allRouters: AppRouteRecordRaw[],
  dynamicRoutes: AppRouteRecordRaw[]
): AppRouteRecordRaw[] {
  const hasDashboard = hasPreferredHome(dynamicRoutes)
  for (const route of allRouters) {
    if (route.name !== 'Root') continue
    route.meta = {
      ...(route.meta || {}),
      hidden: hasDashboard
    }
    for (const child of route.children || []) {
      if (child.name !== WELCOME_HOME_ROUTE_NAME) continue
      child.meta = {
        ...(child.meta || {}),
        title: '首页',
        hidden: hasDashboard
      }
    }
  }
  return allRouters
}
